import { Calendar } from "@/components/ui/calendar";
import { DateRange } from "react-day-picker";
import { cn } from "@/lib/utils";
import { TooltipProvider, Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { isWeekend } from "date-fns";
import { BusyPeriod } from "@/utils/supabaseHelpers";
import { getCountryFlagUrl, getEventColor } from "@/utils/countryUtils";

interface DateRangeSelectorProps {
  selectedDate: DateRange | undefined;
  setSelectedDate: (date: DateRange | undefined) => void;
  busyPeriods?: BusyPeriod[];
  fullWidth?: boolean;
}

export function DateRangeSelector({
  selectedDate,
  setSelectedDate,
  busyPeriods = [],
  fullWidth = false
}: DateRangeSelectorProps) {
  const isDayDisabled = (date: Date) => {
    return isWeekend(date);
  };

  const getBusyPeriodForDate = (date: Date) => {
    const idx = busyPeriods.findIndex(period => {
      const start = new Date(period.start_date);
      const end = new Date(period.end_date);
      return date >= start && date <= end;
    });

    if (idx === -1) return null;
    return { period: busyPeriods[idx], index: idx };
  };

  const fullWidthClassNames = fullWidth ? {
    months: "flex flex-row w-full gap-8",
    month: "flex-1 space-y-4",
    table: "w-full border-collapse space-y-1",
    head_row: "flex justify-around",
    head_cell: "text-muted-foreground rounded-md flex-1 text-center font-normal text-sm",
    row: "flex w-full mt-2 justify-around",
    cell: "relative flex-1 text-center text-sm focus-within:relative focus-within:z-20 border border-border/60 rounded-md [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md",
    day: "h-14 w-full p-0 font-normal aria-selected:opacity-100 hover:bg-accent hover:text-accent-foreground rounded-md inline-flex items-center justify-center text-sm relative",
    caption_label: "text-lg font-medium",
    day_range_middle: "aria-selected:bg-primary/20 aria-selected:text-foreground",
    day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
  } : undefined;

  return (
    <div className={cn(
      "border rounded-lg bg-white/50",
      fullWidth ? "p-4 w-full" : "p-4 px-0"
    )}>
      <Calendar
        mode="range"
        selected={selectedDate}
        onSelect={setSelectedDate}
        defaultMonth={selectedDate?.from}
        numberOfMonths={2}
        className={cn("rounded-md pointer-events-auto", fullWidth && "w-full")}
        classNames={fullWidthClassNames}
        modifiers={{
          disabled: isDayDisabled,
        }}
        components={{
          DayContent: ({ date }) => {
            const result = getBusyPeriodForDate(date);

            if (result) {
              const { period: busyPeriod } = result;
              const color = getEventColor(busyPeriod.country || busyPeriod.id);

              return (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div
                        className="relative flex items-center justify-center w-full h-full rounded-md"
                        style={{ backgroundColor: color.bg }}
                      >
                        {busyPeriod.country && (
                          <img
                            src={getCountryFlagUrl(busyPeriod.country)}
                            alt=""
                            className="absolute top-0 left-0.5 h-4 w-4 rounded-full object-cover z-20"
                          />
                        )}
                        <span className="relative z-10">{date.getDate()}</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-sm font-medium">
                        {busyPeriod.team_scope === "sales" ? "Sales" : "Production"} Busy Period
                      </p>
                      <p className="text-sm">{busyPeriod.description}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              );
            }

            return (
              <div className="flex items-center justify-center w-full h-full">{date.getDate()}</div>
            );
          }
        }}
      />
    </div>
  );
}
